<template>
    <div>
        <div class="flex flex-col min-h-0 h-full p-6 pt-5 | 2xl:p-8 2xl:pt-8">
            <page-header :title="$t('global.mainNav.campaigns')">
                <nio-button
                    color="blue"
                    icon="regular/add"
                    @click="triggerCreate"
                >
                    {{ $t('global.general.add') }}
                </nio-button>
            </page-header>

            <content-box :loading="$wait.is('fetching.campaigns')">
                <template #header>
                    <content-box-tabs :active-tab.sync="filters.tab" :tabs="tabs" />
                    <content-box-search :placeholder="$t('contacts.contactsList.search')" v-model="filters.search" />
                </template>
                <template #body>
                    <nio-table
                        :columns="columns"
                        :rows="campaigns"
                        :selections.sync="selections"
                        ref="body"
                    >
                        <template #no-results>
                            <figure class="mb-6 text-3xl select-none">
                                ¯\_(ツ)_/¯
                            </figure>
                            <p class="mb-2 text-xl">
                                {{ $t('campaigns.listing.noCampaign') }}
                            </p>
                            <p class="mb-4">
                                {{ $t('campaigns.listing.wouldYouLikeToAddOne') }}
                            </p>
                            <nio-button color="blue" icon="regular/add" @click="triggerCreate">
                                {{ $t('global.general.add') }}
                            </nio-button>
                        </template>

                        <template v-slot:[`column-${column.name}`]="{ row: campaign }" v-for="column in columns">
                            <campaign-list-item
                                :campaign="campaign"
                                :column="column.name"
                                :truncate="column.truncate"
                                :key="column.name"
                                @delete="triggerDeleteCampaign(campaign)"
                                @edit="triggerEdit(campaign)"
                            />
                        </template>
                    </nio-table>
                </template>
                <template #footer>
                    <div class="flex-1 flex items-center justify-start p-4">
                        <nio-dropdown :disabled="!selections.length" placement="top-start">
                            <template #trigger>
                                <nio-button color="blue">
                                    {{ $t('contacts.contactsList.bulkAction') }}
                                    <nio-icon class="text-xs ml-2" name="regular/arrow-up-1" />
                                </nio-button>
                            </template>

                            <nio-dropdown-item
                                class="hover:text-red-500"
                                icon="regular/bin-2-alternate"
                                @click="deleteCampaignOpened = true"
                            >
                                {{ $t('global.general.delete') }}
                            </nio-dropdown-item>
                        </nio-dropdown>
                    </div>
                    <div class="flex-1 flex items-center justify-center p-4">
                        <pagination
                            :current-page="pagination.currentPage"
                            :last-page="pagination.lastPage"
                            @update="setCurrentPage"
                            v-if="pagination.total"
                        />
                    </div>
                    <div class="flex-1 flex items-center justify-end p-4">
                        <pagination-info
                            :current="pagination.currentPage"
                            :per-page="perPage"
                            :total="pagination.total"
                            v-if="pagination.total"
                        />
                    </div>
                </template>
            </content-box>
        </div>

        <campaign-modal />
        <confirm-modal
            :opened.sync="deleteCampaignOpened"
            :title="$tc('campaigns.deleteModal.deleteTitle', !empty(toDelete) || selections.length < 2 ? 1 : 2)"
            @confirm="deleteSelected"
        >
            <p slot="message">
                {{ $tc('campaigns.deleteModal.deleteMessage', deleteMessagePluralCount, { number: selections.length }) }}
            </p>
        </confirm-modal>

        <filters-in-url :filters="filters" />
    </div>
</template>

<script>
    import debounce from 'lodash/debounce.js';
    import { mapState, mapActions } from 'vuex';
    import CampaignListItem from '@/views/Campaigns/CampaignListItem.vue';
    import CampaignModal from '@/components/Modals/CampaignModal.vue';
    import ConfirmModal from '@/components/Modals/ConfirmModal.vue';
    import ContentBox from '@/components/ContentBox.vue';
    import ContentBoxSearch from '@/components/ContentBoxSearch.vue';
    import ContentBoxTabs from '@/components/ContentBoxTabs.vue';
    import FiltersInUrl from '@/components/Utils/FiltersInUrl.js';
    import PageHeader from '@/components/PageHeader.vue';
    import Pagination from '@/components/Pagination.vue';
    import PaginationInfo from '@/components/PaginationInfo.vue';

    export default {
        components: {
            CampaignListItem,
            CampaignModal,
            ConfirmModal,
            ContentBox,
            ContentBoxSearch,
            ContentBoxTabs,
            FiltersInUrl,
            PageHeader,
            Pagination,
            PaginationInfo,
        },

        data() {
            return {
                loaded: false,
                selections: [],
                deleteCampaignOpened: false,
                toDelete: null,
                perPage: 25,
                pagination: {
                    from: 0,
                    to: 0,
                    total: 0,
                    currentPage: 1,
                    lastPage: 0,
                },
                filters: {
                    search: '',
                    tab: 'all',
                },
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            ...mapState('campaign', [
                'campaigns',
            ]),

            deleteMessagePluralCount() {
                return !empty(this.toDelete) || this.selections.length < 2 ? 1 : 2;
            },

            tabs() {
                return [
                    {
                        value: 'all',
                        text: `${this.$t('campaigns.listing.tabs.all')}`,
                        icon: 'regular/navigation-menu',
                    },
                ];
            },

            columns() {
                return [
                    {
                        name: 'name',
                        label: this.$t('campaigns.listing.listingHeaders.name'),
                        icon: 'regular/megaphone',
                        className: 'flex-3',
                    },
                    {
                        name: 'createdDate',
                        label: this.$t('campaigns.listing.listingHeaders.createdDate'),
                        icon: 'regular/calendar-3',
                        className: 'flex-2',
                    },
                    {
                        name: 'numberContacts',
                        label: this.$t('campaigns.listing.listingHeaders.numberContacts'),
                        icon: 'regular/multiple-neutral-2',
                        className: 'flex-2',
                    },
                    {
                        name: 'actions',
                        label: this.$t('campaigns.listing.listingHeaders.actions'),
                        icon: 'regular/flash-1',
                        className: 'flex-1',
                        align: 'center',
                        truncate: false,
                    },
                ];
            },
        },

        watch: {
            'filters.tab'() {
                this.selections = [];
            },

            'filters.search'() {
                this.$wait.start('fetching.campaigns');

                if (this.loaded) {
                    this.debounceFetch();
                } else {
                    this.fetch();
                }
            },

            selections(newValue) {
                if (!empty(newValue)) {
                    this.toDelete = null;
                }
            },

            toDelete(newValue) {
                if (!empty(newValue)) {
                    this.selections = [];
                }
            },

            'pagination.currentPage'() {
                this.scrollTop();
            },

            'authUser.account.id': {
                immediate: true,
                handler(newValue) {
                    if (!newValue) {
                        return;
                    }

                    this.fetch();
                },
            },
        },

        methods: {
            ...mapActions('campaign', [
                'getCampaigns',
                'deleteCampaign',
            ]),

            reset() {
                return new Promise(resolve => {
                    this.selections = [];
                    this.toDelete = null;

                    this.$nextTick(() => {
                        resolve();
                    });
                });
            },

            triggerCreate() {
                this.$modal.open('campaign');
            },

            triggerEdit(campaign) {
                this.$modal.open('campaign', { campaign });
            },

            triggerDeleteCampaign(campaign) {
                this.toDelete = campaign.id;
                this.deleteCampaignOpened = true;
            },

            deleteSelected() {
                const campaignIds = this.toDelete ? [this.toDelete] : this.selections;

                try {
                    campaignIds.forEach(id => {
                        this.deleteCampaign(id);
                    });

                    this.$notify.show({
                        type: 'success',
                        message: this.$tc('campaigns.campaignModal.successes.deleted', campaignIds.length),
                    });

                    this.reset();
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$tc('campaigns.campaignModal.errors.deleting', campaignIds.length),
                    });

                    throw error;
                }
            },

            async fetch() {
                this.$wait.start('fetching.campaigns');

                const payload = {
                    search: this.filters.search,
                    page: this.pagination.currentPage,
                    perPage: this.perPage,
                };

                try {
                    const { meta } = await this.getCampaigns(payload);

                    this.pagination.from = meta.from;
                    this.pagination.to = meta.to;
                    this.pagination.total = meta.total;
                    this.pagination.lastPage = meta.last_page;
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$t('campaigns.listing.errors.fetching'),
                    });

                    throw error;
                } finally {
                    this.$wait.end('fetching.campaigns');
                }
            },

            debounceFetch() {
                this.setCurrentPage(1);
            },

            setCurrentPage(page) {
                this.pagination.currentPage = page;
                this.fetch();
            },

            scrollTop() {
                if (this.$refs.body) {
                    this.$refs.body.$el.scrollTop = 0;
                }
            },
        },

        created() {
            this.fetch = debounce(this.fetch, 50);
            this.debounceFetch = debounce(this.debounceFetch, 300);
        },

        mounted() {
            this.$nextTick(() => {
                this.loaded = true;
            });
        },
    };
</script>
